import type { ReactNode } from "react";
import type { RowData } from "@tanstack/react-table";
import { StatusIndicator } from "@design-system/StatusIndicator/StatusIndicator";
import { Tag } from "@design-system/Tag/Tag";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { CellText } from "./CellText";
import type {
    DataGridStatusDisplay,
    DataGridStatusMap,
} from "./cellValueTypes";
import { toDisplayText } from "./cellValues";
import { EmptyCellValue } from "./EmptyCellValue";
import type { DataGridCellProps } from "./types";
import styles from "./cellContent.module.scss";

interface StatusCellProps<
    TData extends RowData,
> extends DataGridCellProps<TData> {
    /** Display per status value; values without an entry show as text. */
    statuses: DataGridStatusMap | undefined;
}

interface StatusBadgeProps {
    display: DataGridStatusDisplay;
    /** The icon variant's label element. */
    label: ReactNode;
}

/** One status value drawn as a Tag, a StatusIndicator dot, or an icon with tinted text. */
const StatusBadge = ({ display, label }: Readonly<StatusBadgeProps>) => {
    switch (display.variant) {
        case "tag":
            return (
                <Tag color={display.color} leftIcon={display.icon}>
                    {display.label}
                </Tag>
            );
        case "indicator":
            return (
                <StatusIndicator
                    variant={display.color}
                    label={display.label}
                    appearance="solid"
                    showText
                />
            );
        case "icon":
            return (
                <span
                    className={styles.statusIcon}
                    data-tone={display.tone ?? "primary"}
                >
                    {display.icon}
                    {label}
                </span>
            );
    }
};

/**
 * Status column cell: maps the value through the column's `statuses` to a
 * Tag, StatusIndicator, or icon and label, with an optional description
 * tooltip. Sort order comes from the column's `order`.
 */
export const StatusCell = <TData extends RowData>({
    context,
    meta,
    statuses,
}: Readonly<StatusCellProps<TData>>) => {
    const value = toDisplayText(context.getValue());
    if (value === null) {
        return <EmptyCellValue empty={meta.empty} />;
    }
    const display = statuses?.[value];
    if (!display) {
        return <CellText text={value} overflow={meta.overflow} />;
    }
    if (!display.description) {
        return (
            <StatusBadge
                display={display}
                label={<CellText text={display.label} overflow="truncate" />}
            />
        );
    }
    // A nested tooltip trigger would swallow hover, so the label skips CellText.
    return (
        <Tooltip content={display.description} side="bottom">
            <span className={styles.inline}>
                <StatusBadge
                    display={display}
                    label={<span className={styles.text}>{display.label}</span>}
                />
            </span>
        </Tooltip>
    );
};
